// src/services/userFormService.js
import api from '../services/api';

/**
 * Загружает список всех доступных проектов
 */
export const fetchAllProjects = async () => {
  try {
    const res = await api.get('/users');
    return res.data.all_projects || [];
  } catch (err) {
    console.error('Ошибка при загрузке проектов:', err);
    return [];
  }
};

/**
 * Валидирует введённые пароли
 */
export const validatePassword = (
  password,
  confirmPassword,
  isEditingSelf,
  isAdmin,
  oldPassword
) => {
  if (!password && !confirmPassword) return { valid: true, error: null };

  if (!password || !confirmPassword) {
    return { valid: false, error: 'Пароли обязательны' };
  }

  if (password !== confirmPassword) {
    return { valid: false, error: 'Пароли не совпадают' };
  }

  if (isAdmin && isEditingSelf && !oldPassword) {
    return { valid: false, error: 'Требуется старый пароль' };
  }

  return { valid: true, error: null };
};

/**
 * Валидация email
 */
export const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

/**
 * Сравнивает два значения (включая массивы)
 */
const isChanged = (a, b) => {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length !== b.length || !a.every((val) => b.includes(val));
  }
  return a !== b;
};

/**
 * Обновляет существующего пользователя через один PATCH-запрос
 * Отправляет только изменённые поля: username, name, surname, email
 */
export const saveUser = async (
  user,
  formData,
  password,
  confirmPassword,
  oldPassword,
  isEditingSelf,
  isAdmin,
  isSuperuser,
  selectedRoles = []
) => {
  const { valid, error } = validatePassword(
    password,
    confirmPassword,
    isEditingSelf,
    isAdmin,
    oldPassword
  );

  if (!valid) {
    return { success: false, error };
  }

  try {
    const initialData = getInitialFormData(user);
    const userId = user.id;
    const updates = {};
    let projectsUpdated = false;

    // Проверяем изменения в полях
    if (isChanged(formData.username, initialData.username)) {
      updates.username = formData.username;
    }
    if (isChanged(formData.name, initialData.name)) {
      updates.name = formData.name;
    }
    if (isChanged(formData.surname, initialData.surname)) {
      updates.surname = formData.surname;
    }
    if (isChanged(formData.email, initialData.email)) {
      updates.email = formData.email;
    }
    if (isChanged(formData.era_name, initialData.era_name)) {
      updates.era_name = formData.era_name;
    }

    // Отправляем все изменения одним запросом, если есть что обновлять
    if (Object.keys(updates).length > 0) {
      const res = await api.patch(`/user/${userId}`, updates);
      if (res.data.message) {
        window.showToast(res.data.message);
      }
    }

    // Обновление статуса admin (отдельный эндпоинт)
    if (isChanged(formData.admin, initialData.admin)) {
      await api.patch(`/user/${userId}/admin`, { admin: formData.admin });
    }

    if (isChanged(formData.analytics_view, initialData.analytics_view)) {
      await api.patch(`/user/${userId}/analytics_view`, { analytics_view: formData.analytics_view });
    }

    // Обновление проектов и ролей (отдельный эндпоинт)
    if (isChanged(formData.projects, initialData.projects) || isChanged(selectedRoles, (user.roles || []).map(r => r.id))) {
      await api.patch(`/user/${userId}/projects`, { 
        projects: formData.projects, 
        role_ids: selectedRoles 
      });
      projectsUpdated = true;
    }

    // Смена пароля
    if (password) {
      const passwordData = {
        new_password: password,
        confirm_password: confirmPassword,
      };

      if (isEditingSelf && (isAdmin || !isSuperuser)) {
        passwordData.old_password = oldPassword;
      }

      const res = await api.post(`/user/${userId}/password`, passwordData);
      if (res.data.message) {
        window.showToast(res.data.message);
      }
    }

    return { success: true };
  } catch (err) {
    const errorMsg = err.response?.data?.error || 'Ошибка при сохранении';
    return { success: false, error: errorMsg };
  }
};

/**
 * Создаёт нового пользователя через POST /register
 */
export const createUser = async (formData, password, confirmPassword, selectedRoles = []) => {
  if (!password || !confirmPassword) {
    return { success: false, error: 'Пароль и подтверждение обязательны' };
  }

  if (password !== confirmPassword) {
    return { success: false, error: 'Пароли не совпадают' };
  }

  if (!formData.username) {
    return { success: false, error: 'Логин обязателен' };
  }

  if (formData.email && !validateEmail(formData.email)) {
    return { success: false, error: 'Неверный формат email' };
  }

  try {
    const userData = {
      ...formData,
      password,
      confirm_password: confirmPassword,
      role_ids: selectedRoles
    };

    // Передаём analytics_view только для superuser
    if (formData.analytics_view !== undefined) {
      userData.analytics_view = formData.analytics_view;
    }
    
    // Передаём era_name
    if (formData.era_name !== undefined) {
      userData.era_name = formData.era_name;
    }
    
    const res = await api.post('/register', userData);
    if (res.data.message) {
      window.showToast(res.data.message);
    }

    return { success: true, user: res.data.user };
  } catch (err) {
    const errorMsg = err.response?.data?.error || 'Ошибка при создании пользователя';
    return { success: false, error: errorMsg };
  }
};

/**
 * Возвращает начальные данные формы
 */
export const getInitialFormData = (user) => {
  if (user && user.id) {
    return {
      username: user.username || '',
      name: user.name || '',
      surname: user.surname || '',
      email: user.email || '',
      admin: user.admin || false,
      era_name: user?.era_name || '',
      analytics_view: user.analytics_view || false,
      projects: Array.isArray(user.projects) ? user.projects.map((p) => p.id) : [],
    };
  }
  return {
    username: '',
    name: '',
    surname: '',
    email: '',
    admin: false,
    era_name: '',
    analytics_view: false,
    projects: [],
  };
};


